/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-named-as-default-member */
import { API } from 'aws-amplify'
import dayjs from 'dayjs'
import { omit, uniqBy } from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import { getItemFee } from './ApiHelpers'
import { doMutation, fetchQuery, retry } from './ApiUtils'
import { generateOptions } from './PaymongoUtils'
import { User as UserApi } from '../API'
import { createUser, createPendingOrder, updateUser, createFreeRegistration } from '../api/mutations'
import { userByEmail, listCategoryFees } from '../api/queries'
import { DEFAULT_ENTRIES_PER_PAGE, FORM_ORDER_OMIT_FIELDS } from '../constants/api'
import { formatDate } from '../constants/formats'
import { CategoryFeeResponse } from '../hooks/useCategoryFees'
import { AttachPaymentIntentRequest } from '../models/Paymongo/PaymentIntent'
import { CreatePaymentMethodRequest } from '../models/Paymongo/PaymentMethod'

type User = Omit<Exclude<UserApi, null>, '__typename'>

export const createPaymentIntent = async (
  eventName: string,
  amount: number,
  metadata: { [key: string]: string },
  idempotencyKey: string,
) => {
  return new Promise((resolve, reject) => {
    const apiName = 'api85b3bd57Payment'
    const path = '/paymongo'
    const myInit = {
      headers: {},
      body: {
        amount,
        payment_method_allowed: ['card', 'paymaya', 'gcash', 'grab_pay'],
        payment_method_options: { card: { request_three_d_secure: 'any' } },
        currency: 'PHP',
        capture_type: 'automatic',
        description: `MEGATechPH Registration - ${eventName}`,
        metadata,
        idempotencyKey,
      },
    }

    API.post(apiName, path, myInit)
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export const doCreatePaymentMethod = async (
  payload: CreatePaymentMethodRequest,
  // times: number,
) => {
  return new Promise((resolve, reject) => {
    // const sampleResponses = {
    //   1: {},
    //   2: {
    //     errors: [
    //       {
    //         code: 'parameter_invalid',
    //         detail: 'details.exp_month must be between 1 and 12.',
    //         source: {
    //           pointer: 'details.exp_month',
    //           attribute: 'exp_month',
    //         },
    //       },
    //       {
    //         code: 'parameter_invalid',
    //         detail:
    //           'details.exp_year must be at least this year or no later than 50 years from now.',
    //         source: {
    //           pointer: 'details.exp_year',
    //           attribute: 'exp_year',
    //         },
    //       },
    //     ],
    //   },
    //   0: undefined,
    // }
    // // const idx = Math.floor(Math.random() * 1)
    // const randomResult = sampleResponses[times]
    // console.log(times, payload, randomResult)
    // if (randomResult !== undefined) resolve(randomResult)
    // else reject('hahai')
    
    try {
      fetch(
        `${process.env.REACT_APP_PAYMONGO_API_URL}/payment_methods`,
        generateOptions(payload),
      )
        .then((response) => response.json())
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    } catch (error) {
      reject(error)
    }
  })
}

export const createPaymentMethod = async (payload: CreatePaymentMethodRequest) => {
  const maxRetries = 3
  const result = await retry(
    async (retries: number) => {
      const response = await doCreatePaymentMethod(payload)
      if (typeof response === 'object' && 'data' in response) return response
      else {
        if (retries < maxRetries - 1)
          throw Error(
            typeof response === 'object' && 'errors' in response
              ? response.errors[0].detail
              : JSON.stringify(response),
          )
        return response
      }
    },
    () => {
      console.log('onRetry called...')
    },
    maxRetries,
  )
  return result
}

export const attachToPaymentIntent = async (
  id: string,
  payload: AttachPaymentIntentRequest,
) => {
  return new Promise((resolve, reject) => {
    try {
      fetch(
        `${process.env.REACT_APP_PAYMONGO_API_URL}/payment_intents/${id}/attach`,
        generateOptions(payload),
      )
        .then((response) => response.json())
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    } catch (error) {
      reject(error)
    }
  })
}

export const updateUserPromise = (cartUser: any, id: string) =>
  new Promise((resolve, reject) => {
    doMutation('updateUser', updateUser, {
      input: {
        id,
        ...omit(cartUser, ['email', 'confirmEmail', 'confirmPhoneNumber']),
      },
    })
      .then(() => {
        resolve(id)
      })
      .catch((error) => reject(error))
  })

export const createUserPromise = (cartUser: any) =>
  new Promise((resolve, reject) => {
    doMutation('createUser', createUser, {
      input: {
        ...omit(cartUser, ['confirmEmail', 'confirmPhoneNumber']),
      },
    })
      .then(({ data }) => {
        const newData = data as unknown as User
        resolve(newData.id)
      })
      .catch((error) => reject(error))
  })

export const doCreatePendingOrder = async (
  cartUser: any,
  cartItems: any[],
  transactionId: string,
  total: number,
  status: string,
  paymentMode: string,
  shippingPreference: string,
  idempotencyKey: string,
) => {
  const createPendingOrderPromise = (currentUserId: string) =>
    new Promise((resolve, reject) => {
      const orderId = uuidv4()
      const order = {
        id: orderId,
        userId: currentUserId,
        eventId: cartItems[0].eventId,
        transactionId,
        total,
        status,
        paymentMode,
        shippingPreference,
        idempotencyKey,
        createdAt: dayjs().tz('Asia/Manila').format(),
      }

      const participants = cartItems.map((cartItem) => ({
        orderId,
        userId: currentUserId,
        ...omit(cartItem, FORM_ORDER_OMIT_FIELDS),
        ...('jerseySize' in cartItem &&
          cartItem.jerseySize.length > 0 && {
            jerseySize: cartItem.jerseySize,
          }),
        ...('relayEvent' in cartItem && {
          relayEvent: cartItem.relayEvent.join(','),
        }),
        birthdate: formatDate(cartItem.birthdate, 'YYYY-MM-DD'),
        ...('tandemBirthdate' in cartItem && {
          tandemBirthdate: formatDate(cartItem.tandemBirthdate, 'YYYY-MM-DD'),
        }),
        createdAt: dayjs().tz('Asia/Manila').format(),
      }))

      const pendingOrderInput = {
        order,
        participants,
      }

      doMutation('createPendingOrder', createPendingOrder, pendingOrderInput)
        .then(() => {
          resolve(currentUserId)
        })
        .catch((error) => reject(error))
    })

  return new Promise((resolve, reject) => {
    fetchQuery('userByEmail', userByEmail, {
      email: cartUser.email,
    })
      .then((userByEmailData) => {
        const { items } = userByEmailData as unknown as { items: User[] }
        if (items.length > 0) return updateUserPromise(cartUser, items[0].id)

        return createUserPromise(cartUser)
      })
      .then((currentUserId: string) => {
        return createPendingOrderPromise(currentUserId)
      })
      .then((currentUserId: string) => resolve(currentUserId))
      .catch((error) => reject(error))
  })
}

export const doCreateFreeRegistration = async (cartUser: any, cartItems: any[]) => {
  const createFreeRegistrationPromise = (currentUserId: string) =>
    new Promise((resolve, reject) => {
      const participants = cartItems.map((cartItem) => ({
        userId: currentUserId,
        ...omit(cartItem, FORM_ORDER_OMIT_FIELDS),
        ...('relayEvent' in cartItem && {
          relayEvent: cartItem.relayEvent.join(','),
        }),
        birthdate: formatDate(cartItem.birthdate, 'YYYY-MM-DD'),
        ...('tandemBirthdate' in cartItem && {
          tandemBirthdate: formatDate(cartItem.tandemBirthdate, 'YYYY-MM-DD'),
        }),
        createdAt: dayjs().tz('Asia/Manila').format(),
      }))

      const freeRegistrationInput = {
        participants,
      }

      doMutation('createFreeRegistration', createFreeRegistration, freeRegistrationInput)
        .then(() => {
          resolve(currentUserId)
        })
        .catch((error) => reject(error))
    })

  return new Promise((resolve, reject) => {
    fetchQuery('userByEmail', userByEmail, {
      email: cartUser.email,
    })
      .then((userByEmailData) => {
        const { items } = userByEmailData as unknown as { items: User[] }
        if (items.length > 0) return updateUserPromise(cartUser, items[0].id)

        return createUserPromise(cartUser)
      })
      .then((currentUserId: string) => {
        return createFreeRegistrationPromise(currentUserId)
      })
      .then((currentUserId: string) => resolve(currentUserId))
      .catch((error) => reject(error))
  })
}

export const fetchCategoryFees = async (categoryIds) => {
  let allItems = []
  let currentNextToken = null
  const filter = {}
  const newCategories = []

  const fetchData = async () => {
    filter['or'] = categoryIds.map((id) => ({ id: { eq: id } }))
    try {
      const { items, nextToken: newNextToken } = (await fetchQuery(
        'listCategories',
        listCategoryFees,
        {
          filter,
          limit: DEFAULT_ENTRIES_PER_PAGE,
          ...(currentNextToken && { nextToken: currentNextToken }),
        },
      )) as unknown as { items: CategoryFeeResponse[]; nextToken }

      allItems = uniqBy([...allItems, ...items], 'id')

      if (newNextToken) {
        currentNextToken = newNextToken
        if (allItems.length < DEFAULT_ENTRIES_PER_PAGE) {
          await fetchData()
        }
        return
      }

      allItems.forEach((i) => {
        if ('event' in i && i.event !== null) {
          const { adminFees, fee } = getItemFee(i)

          newCategories[i.id] = {
            id: i.id,
            fee,
            adminFee: i.adminFee,
            adminFees,
            registrationCutOff: i.event.registrationCutOff,
            bonusParticipants: i.bonusParticipants || 0,
            teamType: i.teamType,
          }
        }
      })
      allItems = []
      currentNextToken = null
      return newCategories
    } catch (e) {
      console.log(e)
      return []
    }
  }

  if (categoryIds !== null && categoryIds.length > 0) {
    await fetchData()
    return newCategories
  }
  return []

// if (categoryIds !== null && categoryIds.length > 0) {
//   const filter = {}
//   filter['or'] = categoryIds.map((id) => ({ id: { eq: id } }))

//   const { items } = (await fetchQuery('listCategories', listCategoryFees, {
//     filter,
//   })) as unknown as { items: CategoryFeeResponse[] }

//   const newCategories = []
//   items.forEach((i) => {
//     if ('event' in i && i.event !== null) {
//       const { adminFees, fee } = getItemFee(i)
//       console.log({ i, adminFees, fee })
//       newCategories[i.id] = {
//         id: i.id,
//         fee,
//         adminFee: i.adminFee,
//         adminFees,
//         registrationCutOff: i.event.registrationCutOff,
//         bonusParticipants: i.bonusParticipants || 0,
//         teamType: i.teamType,
//       }
//     }
//   })
//   return newCategories
// }
// return []
}
